<script setup lang="ts">
import { computed } from 'vue';
import { progressProps } from 'naive-ui';
import GaiaText from './GaiaText.vue';
import { gaiaFonts, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  ...progressProps,
  /** Label above the track, e.g. "Insights credits". */
  label: {
    type: String,
    default: undefined,
  },
  /** Right-aligned monospace value above the track, e.g. "2.3M left". */
  value: {
    type: String,
    default: undefined,
  },
  /**
   * Draws the track as an outlined capsule — a hairline ring on a white
   * track instead of the grey wash — for a meter sitting on a soft surface,
   * where the wash alone is too faint to read as a track.
   */
  bordered: {
    type: Boolean,
    default: false,
  },
});

// `label`/`value`/`bordered` are ours, not n-progress's — keep them off the DOM element.
const passThrough = computed(() => {
  const { label, value, bordered, ...rest } = props;
  return rest;
});

const monoFont = gaiaFonts.mono;
const trackBorder = gaiaNeutral.border;
</script>

<template>
  <!-- The root stays a plain div: GaiaSidebar hides the whole meter when
       collapsed via :deep(.gaia-progress-bar){display:none}, and n-flex would
       write display inline and out-rank that. -->
  <div class="gaia-progress-bar" :class="{ 'gaia-progress-bar--bordered': props.bordered }">
    <n-flex
      v-if="props.label || props.value"
      align="baseline"
      justify="space-between"
      :size="8"
      :wrap="false"
      :style="{ marginBottom: '8px' }"
    >
      <gaia-text tone="tertiary">{{ props.label }}</gaia-text>
      <gaia-text v-if="props.value" size="control" tone="text" :style="{ fontFamily: monoFont }">
        {{ props.value }}
      </gaia-text>
    </n-flex>
    <n-progress
      v-bind="passThrough"
      type="line"
      :show-indicator="false"
      :height="props.height ?? 4"
      :border-radius="props.borderRadius ?? 3"
      :color="props.color ?? gaiaNeutral.ink"
      :rail-color="
        props.railColor ?? (props.bordered ? gaiaNeutral.surface : gaiaNeutral.surfaceTrack)
      "
    />
  </div>
</template>

<style scoped>
/* n-progress has no border of its own; the ring is a shadow so the track
   keeps its height and radius exactly. */
.gaia-progress-bar--bordered :deep(.n-progress-graph-line-rail) {
  box-shadow: 0 0 0 1px v-bind(trackBorder);
}
</style>
